<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="optionsData"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
  >
    <v-row
      class="term-graph-container ma-0 pa-0"
      justify="center"
    >
      <network
        v-if="nodes.length"
        ref="network"
        :key="networkId"
        class="network"
        :nodes="nodes"
        :edges="edges"
        :options="options"
      />
    </v-row>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import Vue from 'vue';
import { Network } from 'vue-vis-network';
import colors from '@/utils/colors';
const {
  hotnessColor,
  graphColor,
} = colors;
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import { generateRandomId } from '@/utils/math';
import Badge from '@/components/Badge';

import 'vis-network/dist/vis-network.min.css';

Vue.component('network', Network);

export default {
  name: 'TermGraph',
  components: {
    CardWithTitle,
    Badge,
  },
  mixins: [
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    graphData: {
      type: Object,
      required: true,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      nodes: [],
      edges: [],
      networkId: generateRandomId(),
      exportItems: [{
        action: () => this.exportGraph(),
        title: this.$t('components.models.TermGraph.label.exportPng'),
      }],
    };
  },
  computed: {
    edgeColor() {
      return this.$vuetify.theme.dark
        ? graphColor.dark.edge
        : graphColor.light.edge;
    },
    nodeColor() {
      return this.$vuetify.theme.dark
        ? graphColor.dark.node
        : graphColor.light.node;
    },
    config() {
      return {
        edges: {
          width: 1,
          color: this.edgeColor,
          smooth: {
            type: 'continuous',
          },
        },
        nodes: {
          shape: 'dot',
          font: {
            color: this.nodeColor,
          },
          scaling: {
            max: 25,
            min: 10,
            label: {
              max: 25,
              min: 25,
            },
          },
        },
        physics: {
          forceAtlas2Based: {
            avoidOverlap: 1,
            damping: 1,
          },
          solver: 'forceAtlas2Based',
        },
      };
    },
    options() {
      return {
        interaction: {
          dragView: this.isModal,
          hoverConnectedEdges: false,
          tooltipDelay: 50,
          zoomView: this.isModal,
        },
        edges: this.config.edges,
        nodes: this.config.nodes,
        physics: this.config.physics,
      };
    },
    optionsData() {
      return this.baseCardOptions.concat(this.exportOptionsData).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
    },
  },
  watch: {
    graphData: {
      immediate: true,
      handler: 'createGraph',
    },
  },
  methods: {
    createGraph() {
      if (this.graphData?.children) {
        this.nodes = [];
        this.edges = [];
        this.createGraphNode(this.graphData);
        this.networkId = generateRandomId();
      }
    },
    createGraphNode(node, parentNode = null) {
      this.nodes.push({
        label: node.name,
        id: node.id,
        value: node.data.mentions,
        color: this.getHotColorByPerc(node.data.normalized_acceleration),
      });

      if (parentNode) {
        this.edges.push({
          to: parentNode.id,
          from: node.id,
        });
      }

      node.children.map(nodeOfNodes => {
        return this.createGraphNode(nodeOfNodes, node);
      });
    },
    getHotColorByPerc(value) {
      let color;
      if (value < 0.2) {
        color = hotnessColor[0]; //0-10%
      } else if (value < 0.4) {
        color = hotnessColor[1]; //10-20%
      } else if (value < 0.8) {
        color = hotnessColor[2]; //20-40%
      } else if (value < 1.2) {
        color = hotnessColor[3]; //40-60%
      } else if (value < 1.6) {
        color = hotnessColor[4]; //60-80%
      } else {
        color = hotnessColor[5]; //80-100%
      }
      return color;
    },
  },
};
</script>

<style scoped lang="scss">
  .term-graph-container {
    vertical-align: top;
    height: 90%;
    width: 100%;
  }

  ::v-deep {
    .vis-button {
      background-color: white;
    }

    .vis-network {
      outline: -webkit-focus-ring-color auto 0 !important;
    }
  }
</style>
